/**
 * MergeUnitsBanner
 * 
 * Conditional banner shown on the Channels List page when there are
 * Msool units but none have non-Airbnb channels linked (Booking/Gathern).
 * Prompts the user to go to the units mapping page to merge units.
 */

import { FC, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { Box, Typography, Button } from '@mui/material';
import { T_KEYS } from '../../constants';

const BannerContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '32px',
  marginBottom: '24px',
  borderRadius: '16px',
  border: '1px solid rgba(91, 47, 249, 0.1)',
  background: 'linear-gradient(174deg, rgba(91, 47, 249, 0.05) 0%, rgba(124, 58, 237, 0.05) 100%)',
});

const BannerContent = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
});

const TextSection = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  gap: '8px',
});

const BannerTitle = styled(Typography)({
  fontSize: '20px',
  lineHeight: '24px',
  color: '#5B21B6',
  fontFamily: '"Tajawal", sans-serif',
});

const BannerDescription = styled(Typography)({
  fontSize: '14px',
  lineHeight: '22.4px',
  color: '#666666',
  fontFamily: '"SF Arabic", "Tajawal", sans-serif',
});

const CTAButton = styled(Button)({
  height: '48px',
  minWidth: '225px',
  borderRadius: '8px',
  backgroundColor: '#5B3FFF',
  color: '#FFFFFF',
  fontSize: '16px',
  fontWeight: 700,
  fontFamily: '"Tajawal", sans-serif',
  textTransform: 'none',
  letterSpacing: '-0.3125px',
  whiteSpace: 'nowrap',
  '&:hover': {
    backgroundColor: '#4A2FE0',
  },
});

const MergeUnitsBanner: FC = memo(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleNavigateToMapping = () => {
    navigate('/channels/units-mapping');
  };

  return (
    <BannerContainer>
      <BannerContent>
        <TextSection>
          <BannerTitle>{t(T_KEYS.MERGE_BANNER_TITLE)}</BannerTitle>
          <BannerDescription>{t(T_KEYS.MERGE_BANNER_DESC)}</BannerDescription>
        </TextSection>
        <CTAButton variant="contained" onClick={handleNavigateToMapping}>
          {t(T_KEYS.MERGE_BANNER_CTA)}
        </CTAButton>
      </BannerContent>
    </BannerContainer>
  );
});

MergeUnitsBanner.displayName = 'MergeUnitsBanner';

export default MergeUnitsBanner;
